import React, {useEffect, useState} from "react";
import Header from "../../components/header/header";
import PageTitle from "../../components/page_title/page_title";
import EmptyPlaceholder from "../../components/empty_placeholder/empty_placeholder";
import axiosInstance from "../../AxiosInstance";
import {formatTimestamp} from "../../components/utils";
import styles from "../../styles/dashboard.module.css";
import {useTranslation} from "react-i18next";
import PaginationControls, {getStoredPageSize} from "../../components/pagination/PaginationControls";

export default function HistoricList() {
    const {t} = useTranslation();
    const [rows, setRows] = useState([]);
    const [page, setPage] = useState(1);
    const [totalCount, setTotalCount] = useState(0);
    const [dateFrom, setDateFrom] = useState('');
    const [dateTo, setDateTo] = useState('');
    const [loading, setLoading] = useState(false);
    const [pageSize, setPageSize] = useState(getStoredPageSize);

    const fetchData = (pageNum, from, to, size) => {
        setLoading(true);
        const ps = size !== undefined ? size : pageSize;
        let url = `historic_changes/?page=${pageNum}&page_size=${ps}`;
        if (from) url += `&date_from=${from}`;
        if (to) url += `&date_to=${to}`;

        axiosInstance.get(url).then((response) => {
            setRows(response.data.results);
            setTotalCount(response.data.count);
            setLoading(false);
        }).catch(() => {
            setRows([]);
            setTotalCount(0);
            setLoading(false);
        });
    };

    useEffect(() => {
        fetchData(page, dateFrom, dateTo);
    }, [page, pageSize]);

    const handleFilter = () => {
        setPage(1);
        fetchData(1, dateFrom, dateTo);
    };

    const handleClearFilters = () => {
        setDateFrom('');
        setDateTo('');
        setPage(1);
        fetchData(1, '', '');
    };

    const handlePageSizeChange = (newSize) => {
        setPageSize(newSize);
        setPage(1);
    };

    const totalPages = Math.ceil(totalCount / pageSize);

    return (
        <div className={styles.dashboard_container}>
            <Header sidebarIndex={6}/>

            <div className={'mx-5 mb-4'}>
                <PageTitle title={t('historic.title')}/>

                <div style={{display: 'flex', alignItems: 'center', gap: '12px', marginTop: '12px', marginBottom: '16px', flexWrap: 'wrap'}}>
                    <div style={{display: 'flex', alignItems: 'center', gap: '6px'}}>
                        <label style={{fontSize: '13px', color: '#64748B', fontWeight: 500}}>{t('historic.from')}</label>
                        <input
                            type="date"
                            value={dateFrom}
                            onChange={(e) => setDateFrom(e.target.value)}
                            style={{padding: '6px 10px', borderRadius: '6px', border: '1px solid #E2E8F0', fontSize: '13px', color: '#334155'}}
                        />
                    </div>
                    <div style={{display: 'flex', alignItems: 'center', gap: '6px'}}>
                        <label style={{fontSize: '13px', color: '#64748B', fontWeight: 500}}>{t('historic.to')}</label>
                        <input
                            type="date"
                            value={dateTo}
                            onChange={(e) => setDateTo(e.target.value)}
                            style={{padding: '6px 10px', borderRadius: '6px', border: '1px solid #E2E8F0', fontSize: '13px', color: '#334155'}}
                        />
                    </div>
                    <button
                        onClick={handleFilter}
                        style={{padding: '6px 16px', borderRadius: '6px', backgroundColor: '#4F46E5', color: '#FFF', border: 'none', fontSize: '13px', cursor: 'pointer', fontWeight: 500}}
                    >
                        {t('historic.filter')}
                    </button>
                    {(dateFrom || dateTo) && (
                        <button
                            onClick={handleClearFilters}
                            style={{padding: '6px 16px', borderRadius: '6px', backgroundColor: '#F1F5F9', color: '#64748B', border: '1px solid #E2E8F0', fontSize: '13px', cursor: 'pointer', fontWeight: 500}}
                        >
                            {t('historic.clear')}
                        </button>
                    )}
                    <span style={{fontSize: '12px', color: '#94A3B8', marginLeft: 'auto'}}>{t('historic.results', {count: totalCount})}</span>
                </div>

                {loading ? (
                    <div style={{textAlign: 'center', padding: '40px', color: '#94A3B8'}}>{t('common.loading')}</div>
                ) : rows.length > 0 ? (
                    <>
                        <div style={{overflowX: 'auto'}}>
                            <table style={{width: '100%', borderCollapse: 'collapse', backgroundColor: '#FFF', borderRadius: '8px', overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.08)'}}>
                                <thead>
                                    <tr style={{backgroundColor: '#F8FAFC', borderBottom: '1px solid #E2E8F0'}}>
                                        <th style={{padding: '10px 16px', textAlign: 'center', fontSize: '13px', fontWeight: 600, color: '#64748B'}}>{t('historic.user')}</th>
                                        <th style={{padding: '10px 16px', textAlign: 'center', fontSize: '13px', fontWeight: 600, color: '#64748B'}}>{t('historic.action')}</th>
                                        <th style={{padding: '10px 16px', textAlign: 'center', fontSize: '13px', fontWeight: 600, color: '#64748B'}}>{t('historic.timestamp')}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {rows.map((row, index) => (
                                        <tr key={index} style={{borderBottom: '1px solid #F1F5F9'}}>
                                            <td style={{padding: '10px 16px', fontSize: '13px', color: '#334155', textAlign: 'center'}}>{row.user?.username || '-'}</td>
                                            <td style={{padding: '10px 16px', fontSize: '13px', color: '#334155', textAlign: 'center'}}>{row.action}</td>
                                            <td style={{padding: '10px 16px', fontSize: '13px', color: '#94A3B8', textAlign: 'center'}}>{formatTimestamp(row.timestamp)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        <PaginationControls
                            page={page}
                            totalPages={totalPages}
                            pageSize={pageSize}
                            onPageChange={setPage}
                            onPageSizeChange={handlePageSizeChange}
                            totalCount={totalCount}
                        />
                    </>
                ) : (
                    <EmptyPlaceholder message={t('historic.noData')}/>
                )}
            </div>
        </div>
    )
}
